/**
 * Servicing Pipeline Types
 *
 * Types for tracking loans in interim servicing where Lendwise
 * collects mortgage payments before investor servicing transfer.
 */

// --- Status Types ---

export type ServicingLoanStatus = 'active' | 'completed' | 'transferred';
export type ServicingPaymentStatus = 'scheduled' | 'due' | 'received' | 'late' | 'partial' | 'waived' | 'bounced';
export type ServicingPaymentReversalReason = 'nsf' | 'chargeback' | 'bank_reject' | 'borrower_revoked' | 'other';

export function isSettledPaymentStatus(status: string | null | undefined): boolean {
  return status === 'received' || status === 'waived';
}

export function isUnsettledPaymentStatus(status: string | null | undefined): boolean {
  return !isSettledPaymentStatus(status);
}

// --- Database Types ---

export interface ServicingLoan {
  id: string;
  organization_id: string;
  encompass_loan_id: string;
  banker_recon_id: string | null;
  loan_number: string;
  borrower_name: string | null;
  loan_amount: number;
  interest_rate: number;
  loan_term_months: number | null;
  closing_date: string | null;
  funded_date: string | null;
  first_payment_date: string;
  investor_purchase_date: string | null;
  first_payment_to_investor: string | null;
  servicing_transfer_date: string | null;
  investor_name: string | null;
  /** Combined "LO / LO2" display string resolved from encompass_loans at query time. */
  loan_officer_display?: string | null;
  prepaid_interest: number | null;
  status: ServicingLoanStatus;
  interim_servicing_data: Record<string, unknown> | null;
  last_sync_at: string | null;
  sync_error: string | null;
  payment_count: number;
  total_expected_payments: number | null;
  total_received_payments: number;
  total_principal_collected: number;
  total_interest_collected: number;
  total_escrow_collected: number;
  net_servicing_income: number;
  investor_template_id: string | null;
  /** Earliest unsettled payment due date (persisted by recalculateLoanTotals). */
  next_payment_due_date: string | null;
  /** Count of payments with status='received' (persisted by recalculateLoanTotals). */
  received_payment_count: number;
  /** True if any unsettled payment has due_date < today (persisted by recalculateLoanTotals). */
  has_overdue_payments: boolean;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
  /** Discriminant for virtual rows projected from encompass_loans (upcoming loans). */
  _source?: 'servicing_loans' | 'encompass_loans';
}

/** Review flags are explicit; unknown amounts must never appear as zero-dollar payments. */
export function needsPaymentReview(loan: Pick<ServicingLoan, 'sync_error' | 'total_expected_payments'>): boolean {
  return loan.total_expected_payments === null || loan.sync_error?.startsWith('PAYMENT_') === true;
}

export interface ServicingPayment {
  id: string;
  servicing_loan_id: string;
  payment_number: number;
  due_date: string;
  principal_due: number;
  interest_due: number;
  escrow_due: number;
  total_amount_due: number;
  interest_rate: number | null;
  payment_received_date: string | null;
  principal_received: number;
  interest_received: number;
  escrow_received: number;
  total_amount_received: number;
  late_fee: number;
  payment_method: string | null;
  reference_number: string | null;
  status: ServicingPaymentStatus;
  reversed_at?: string | null;
  reversal_reason?: ServicingPaymentReversalReason | null;
  reversal_notes?: string | null;
  reversed_by?: string | null;
  reversed_payment_received_date?: string | null;
  reversed_principal_received?: number | null;
  reversed_interest_received?: number | null;
  reversed_escrow_received?: number | null;
  reversed_total_amount_received?: number | null;
  reversed_payment_method?: string | null;
  reversed_reference_number?: string | null;
  encompass_guid: string | null;
  source: 'encompass' | 'manual';
  notes: string | null;
  recorded_by: string | null;
  created_at: string;
  updated_at: string;
}

/** A received monthly installment joined to its completed servicing loan. */
export interface CompletedServicingPayment {
  payment: ServicingPayment;
  loan: ServicingLoan;
}

// --- Input Types ---

export interface CreateServicingLoanInput {
  organization_id: string;
  encompass_loan_id: string;
  banker_recon_id?: string;
  loan_number: string;
  borrower_name?: string;
  loan_amount: number;
  interest_rate: number;
  loan_term_months?: number;
  closing_date?: string;
  funded_date?: string;
  first_payment_date: string;
  investor_purchase_date?: string;
  first_payment_to_investor?: string;
  investor_name?: string;
  created_by?: string;
}

export interface RecordPaymentInput {
  servicing_loan_id: string;
  payment_number: number;
  payment_received_date: string;
  principal_received: number;
  interest_received: number;
  escrow_received: number;
  total_amount_received: number;
  late_fee?: number;
  payment_method?: string;
  reference_number?: string;
  notes?: string;
  recorded_by?: string;
}

export interface ReverseServicingPaymentInput {
  servicing_loan_id: string;
  payment_number: number;
  reversed_at: string;
  reversal_reason: ServicingPaymentReversalReason;
  reversal_notes?: string;
  reversed_by?: string;
}

// --- Dashboard Types ---

export interface ServicingSummaryMetrics {
  totalActiveLoans: number;
  totalExpectedPayments: number;
  totalCollected: number;
  totalOutstanding: number;
  netServicingIncome: number;
  overduePaymentsCount: number;
}

// --- Payment Reminder Types ---

export interface PaymentReminderLoan {
  encompassLoanId: string;
  servicingLoanId: string;
  loanNumber: string;
  borrowerName: string;
  amountDue: number;
  dueDate: string;
  paymentStatus: 'upcoming' | 'overdue';
  daysOverdue?: number;
}

export interface PaymentReminderResult {
  success: boolean;
  summary?: { sent: number; failed: number; skipped: number; total: number };
  results?: Array<{
    loanNumber: string;
    status: 'sent' | 'failed' | 'skipped';
    reason?: string;
    emailId?: string;
    borrowerEmail?: string;
  }>;
  error?: string;
}

// --- Payment Received Confirmation Types ---

export interface PaymentReceivedPayload {
  encompassLoanId: string;
  servicingLoanId: string;
  loanNumber: string;
  paymentNumber: number;
  amountReceived: number;
  principalReceived: number;
  interestReceived: number;
  escrowReceived: number;
  dueDate: string;
  dateReceived: string;
  paymentMethod?: string;
  referenceNumber?: string;
  nextPaymentDueDate?: string;
  nextPaymentAmount?: number;
}

export interface PaymentReceivedResult {
  success: boolean;
  emailId?: string;
  borrowerEmail?: string;
  skipped?: boolean;
  reason?: string;
  error?: string;
}

// --- Form 1098 Data (from encompass_loans + company_profiles) ---

export interface Form1098Data {
  property_address: string | null;
  subject_property_city: string | null;
  subject_property_state: string | null;
  property_zip: string | null;
  closing_date: string | null;
  proposed_mi_payment: number | null;
  borrower_mailing_street: string | null;
  borrower_mailing_city: string | null;
  borrower_mailing_state: string | null;
  borrower_mailing_zip: string | null;
  borrower_ssn_last4: string | null;
  investor_loan_number: string | null;
  // Lender info (from company_profiles)
  lender_name: string | null;
  lender_tin: string | null;
  lender_address: string | null;
  lender_city_state_zip: string | null;
  lender_phone: string | null;
}

// --- Payment Schedule Generation ---

export interface PaymentScheduleEntry {
  payment_number: number;
  due_date: string;
  principal_due: number;
  interest_due: number;
  escrow_due: number;
  total_amount_due: number;
  interest_rate: number;
  remaining_balance: number;
}

// --- Helpers ---

/**
 * Calculate monthly P&I payment using standard amortization formula.
 * M = P * [r(1+r)^n] / [(1+r)^n - 1]
 */
export function calculateMonthlyPI(
  loanAmount: number,
  annualRate: number,
  termMonths: number
): number {
  if (termMonths <= 0) {
return 0;
}
  const monthlyRate = annualRate / 100 / 12;
  if (monthlyRate === 0) {
return loanAmount / termMonths;
}
  const factor = Math.pow(1 + monthlyRate, termMonths);
  return loanAmount * (monthlyRate * factor) / (factor - 1);
}

/**
 * Calculate the number of serviced payments between two dates.
 * Returns the count of monthly payment dates from firstPaymentDate
 * up to (but not including) firstPaymentToInvestor.
 */
export function calculatePaymentCount(
  firstPaymentDate: string,
  firstPaymentToInvestor: string
): number {
  const first = new Date(firstPaymentDate);
  const last = new Date(firstPaymentToInvestor);
  if (last <= first) {
return 0;
}

  const monthsDiff =
    (last.getFullYear() - first.getFullYear()) * 12 +
    (last.getMonth() - first.getMonth());

  return Math.max(0, monthsDiff);
}

function isValidDateInput(value: string): boolean {
  if (typeof value !== 'string' || value.trim() === '') {
    return false;
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    return false;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));

  return date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day;
}

function roundToCents(value: number): number {
  return Math.round(value * 100) / 100;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Calculate the principal reduction used by banker recon investor-wire math.
 *
 * This intentionally uses exact amortized ending-balance math instead of the
 * penny-rounded servicing payment schedule. Servicing payment rows still use
 * generatePaymentSchedule().
 *
 * `interestOnlyTermMonths` (optional, default 0) is the loan's interest-only
 * period in months. Interest-only loans collect ZERO scheduled principal during
 * that period, so when every serviced payment falls inside it the reduction is 0.
 * With the default 0 the function is identical to the fully-amortizing behavior.
 */
export function calculateInvestorWirePrincipalReduction(
  loanAmount: number,
  annualRate: number,
  termMonths: number,
  firstPaymentDate: string,
  firstPaymentToInvestor: string,
  interestOnlyTermMonths = 0
): number | null {
  if (!Number.isFinite(loanAmount) || loanAmount <= 0) {
    return null;
  }
  if (!Number.isFinite(annualRate) || annualRate < 0) {
    return null;
  }
  if (!Number.isFinite(termMonths) || termMonths <= 0) {
    return null;
  }
  if (!isValidDateInput(firstPaymentDate) || !isValidDateInput(firstPaymentToInvestor)) {
    return null;
  }

  const paymentCount = calculatePaymentCount(firstPaymentDate, firstPaymentToInvestor);
  if (!Number.isFinite(paymentCount) || paymentCount < 0) {
    return null;
  }
  if (paymentCount === 0) {
    return 0;
  }

  // Interest-only loans accrue ZERO scheduled principal during the interest-only
  // period. In banker recon the serviced window (first payment → first-payment-to-
  // investor) is 1–2 payments while IO terms are ~120 months, so the window is
  // always inside the IO period. We intentionally do NOT model post-IO recast here
  // (generatePaymentSchedule() uses a single monthlyPI and does not recast either),
  // so a window crossing the IO boundary falls through to the amortizing math below.
  const ioMonths = Number.isFinite(interestOnlyTermMonths) && interestOnlyTermMonths > 0
    ? Math.floor(interestOnlyTermMonths)
    : 0;
  if (ioMonths >= paymentCount) {
    return 0;
  }

  const monthlyRate = annualRate / 100 / 12;
  let principalReduction: number;

  if (monthlyRate === 0) {
    principalReduction = (loanAmount / termMonths) * paymentCount;
  } else {
    const monthlyPI = calculateMonthlyPI(loanAmount, annualRate, termMonths);
    if (!Number.isFinite(monthlyPI) || monthlyPI <= 0) {
      return null;
    }

    const factor = Math.pow(1 + monthlyRate, paymentCount);
    const endingBalance = loanAmount * factor - monthlyPI * ((factor - 1) / monthlyRate);
    principalReduction = loanAmount - endingBalance;
  }

  return roundToCents(clamp(principalReduction, 0, loanAmount));
}

/**
 * Enumerate the canonical monthly due dates between `firstPaymentDate`
 * (inclusive) and `firstPaymentToInvestor` (exclusive). Uses UTC month
 * arithmetic so the result is timezone-independent and matches
 * `nextMonthlyDueDate` (which already advances via setUTCMonth).
 *
 * IMPORTANT: this is the single source of truth for the due-date sequence.
 * `generatePaymentSchedule` and the Goodbye Letter's schedule-completeness
 * check both call this helper so generator and validator can never drift.
 */
export function enumeratePaymentDueDates(
  firstPaymentDate: string,
  firstPaymentToInvestor: string
): string[] {
  const count = calculatePaymentCount(firstPaymentDate, firstPaymentToInvestor);
  if (count === 0) {
return [];
}

  const start = new Date(firstPaymentDate);
  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(start);
    d.setUTCMonth(d.getUTCMonth() + i);
    out.push(d.toISOString().split('T')[0]);
  }
  return out;
}

/**
 * Generate a payment schedule between firstPaymentDate and firstPaymentToInvestor.
 */
export function generatePaymentSchedule(
  loanAmount: number,
  annualRate: number,
  termMonths: number,
  monthlyPI: number,
  monthlyEscrow: number,
  firstPaymentDate: string,
  firstPaymentToInvestor: string
): PaymentScheduleEntry[] {
  const dueDates = enumeratePaymentDueDates(firstPaymentDate, firstPaymentToInvestor);
  if (dueDates.length === 0) {
return [];
}
  const count = dueDates.length;

  const monthlyRate = annualRate / 100 / 12;
  const schedule: PaymentScheduleEntry[] = [];
  let remainingBalance = loanAmount;

  for (let i = 0; i < count; i++) {
    const dueDateStr = dueDates[i];

    // Encompass convention: round interest first, derive principal as remainder
    let interestDue = Math.round(remainingBalance * monthlyRate * 100) / 100;
    let principalDue = Math.round((monthlyPI - interestDue) * 100) / 100;

    // Snap near-zero principal (floating-point artifact on IO loans)
    if (Math.abs(principalDue) < 0.015) {
      principalDue = 0;
      interestDue = Math.round(monthlyPI * 100) / 100;
    }

    // Terminal payment reconciliation: only when generating full amortization
    if (i === count - 1 && count >= termMonths) {
      principalDue = Math.round(remainingBalance * 100) / 100;
    }

    const totalDue = Math.round((monthlyPI + monthlyEscrow) * 100) / 100;

    schedule.push({
      payment_number: i + 1,
      due_date: dueDateStr,
      principal_due: principalDue,
      interest_due: interestDue,
      escrow_due: Math.round(monthlyEscrow * 100) / 100,
      total_amount_due: totalDue,
      interest_rate: annualRate,
      remaining_balance: Math.round((remainingBalance - principalDue) * 100) / 100
    });

    remainingBalance -= principalDue;
  }

  return schedule;
}

/**
 * Compute the next monthly due date from a previous due date.
 * Advances by one calendar month (same day-of-month).
 *
 * Mirrors supabase/functions/_shared/servicing-math.ts:nextMonthlyDueDate
 */
export function nextMonthlyDueDate(previousDueDate: string): string {
  const d = new Date(previousDueDate);
  d.setUTCMonth(d.getUTCMonth() + 1);
  return d.toISOString().split('T')[0];
}

/**
 * Generate a single amortization payment entry.
 * Uses interest-first rounding to match Encompass convention.
 *
 * Mirrors supabase/functions/_shared/servicing-math.ts:generateSinglePayment
 */
export function generateSinglePayment(
  remainingBalance: number,
  annualRate: number,
  monthlyPI: number,
  monthlyEscrow: number,
  dueDate: string,
  paymentNumber: number
): PaymentScheduleEntry {
  const monthlyRate = annualRate / 100 / 12;

  let interestDue = Math.round(remainingBalance * monthlyRate * 100) / 100;
  let principalDue = Math.round((monthlyPI - interestDue) * 100) / 100;

  // Snap near-zero principal (floating-point artifact on IO loans)
  if (Math.abs(principalDue) < 0.015) {
    principalDue = 0;
    interestDue = Math.round(monthlyPI * 100) / 100;
  }

  const totalDue = Math.round((monthlyPI + monthlyEscrow) * 100) / 100;

  return {
    payment_number: paymentNumber,
    due_date: dueDate,
    principal_due: principalDue,
    interest_due: interestDue,
    escrow_due: Math.round(monthlyEscrow * 100) / 100,
    total_amount_due: totalDue,
    interest_rate: annualRate,
    remaining_balance: Math.round((remainingBalance - principalDue) * 100) / 100
  };
}

// --- Amortization Document Validation ---

export interface ParsedAmortizationEntry {
  paymentNumber: number;
  paymentDate: string;
  paymentAmount: number;
  principalPortion: number;
  interestPortion: number;
  endingBalance: number;
}

/** Parse the markdown table from a Docling-extracted Amortization Schedule. */
export function parseAmortizationMarkdown(text: string): ParsedAmortizationEntry[] {
  const lines = text.split('\n').filter(l => l.trim().startsWith('|'));
  if (lines.length < 3) {
return [];
} // need header + separator + at least 1 data row

  // Skip header and separator rows
  const dataLines = lines.slice(2);
  const entries: ParsedAmortizationEntry[] = [];

  for (const line of dataLines) {
    const cells = line.split('|').map(c => c.trim()).filter(Boolean);
    // Expect at least 6 columns: Pmt.#, Pmt.Date, Payment Amount, Principal, Interest, Ending Balance
    if (cells.length < 6) {
continue;
}

    const paymentNumber = parseInt(cells[0], 10);
    if (isNaN(paymentNumber) || paymentNumber <= 0) {
continue;
}

    const parseCurrency = (s: string): number => {
      const cleaned = s.replace(/[$,]/g, '').trim();
      const n = parseFloat(cleaned);
      return isNaN(n) ? -1 : n;
    };

    const paymentAmount = parseCurrency(cells[2]);
    const principalPortion = parseCurrency(cells[3]);
    const interestPortion = parseCurrency(cells[4]);
    const endingBalance = parseCurrency(cells[5]);

    // Skip malformed rows (OCR artifacts — multiple values merged into one cell)
    if (paymentAmount < 0 || principalPortion < 0 || interestPortion < 0 || endingBalance < 0) {
continue;
}

    entries.push({
      paymentNumber,
      paymentDate: cells[1],
      paymentAmount,
      principalPortion,
      interestPortion,
      endingBalance
    });
  }

  return entries;
}

/** Validate formula schedule against document-parsed schedule. Returns mismatches. */
export function validateScheduleAgainstDocument(
  formulaSchedule: PaymentScheduleEntry[],
  documentSchedule: ParsedAmortizationEntry[]
): { paymentNumber: number; field: string; formula: number; document: number }[] {
  const docMap = new Map(documentSchedule.map(e => [e.paymentNumber, e]));
  const mismatches: { paymentNumber: number; field: string; formula: number; document: number }[] = [];
  const TOLERANCE = 0.01;

  for (const entry of formulaSchedule) {
    const doc = docMap.get(entry.payment_number);
    if (!doc) {
continue;
}

    if (Math.abs(entry.principal_due - doc.principalPortion) > TOLERANCE) {
      mismatches.push({ paymentNumber: entry.payment_number, field: 'principal', formula: entry.principal_due, document: doc.principalPortion });
    }
    if (Math.abs(entry.interest_due - doc.interestPortion) > TOLERANCE) {
      mismatches.push({ paymentNumber: entry.payment_number, field: 'interest', formula: entry.interest_due, document: doc.interestPortion });
    }
    if (Math.abs(entry.remaining_balance - doc.endingBalance) > TOLERANCE) {
      mismatches.push({ paymentNumber: entry.payment_number, field: 'balance', formula: entry.remaining_balance, document: doc.endingBalance });
    }
  }

  return mismatches;
}

/**
 * Safe numeric coercion (matches banker-recon pattern).
 */
export function toNumber(v: unknown, fallback = 0): number {
  if (typeof v === 'number') {
return Number.isFinite(v) ? v : fallback;
}
  const cleaned = typeof v === 'string' ? v.replace(/,/g, '') : v;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : fallback;
}

// --- Servicer Contact (for RESPA transfer notices) ---

export interface ServicerContact {
  name: string;
  department?: string;
  attentionTo?: string;
  address: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
  phone?: string;
  tollFreePhone?: string;
  email?: string;
  website?: string;
  hours?: string;
}

// --- Servicing Transfer Notice Data ---

export interface ServicingTransferNoticeData {
  // Loan identification
  loanNumber: string;
  investorLoanNumber: string | null;

  // Borrower
  borrowerName: string;
  coBorrowerName: string | null;
  borrowerMailingAddress: string[];
  propertyAddress: string[];

  // Current servicer (Lendwise)
  currentServicer: ServicerContact;

  // New servicer (investor's sub-servicer)
  newServicer: ServicerContact;

  // Key dates
  noticeDate: string;
  loanSoldDate: string | null;
  transferEffectiveDate: string;
  lastPaymentDateCurrentServicer: string;
  firstPaymentDateNewServicer: string;

  // Payment details (for coupons)
  principalAndInterest: number;
  escrowTotal: number;
  escrowBreakdown: { insurance: number; taxes: number; mi: number } | null;
  totalMonthlyPayment: number;
  hasEscrow: boolean;

  // Coupon-specific
  coupon1DueDate: string;
  coupon2DueDate: string;
}
